export type RawSearchValue = string | string[];
export type RawSearch = Record<string, RawSearchValue>;

function decode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

export function parseSearch(searchStr: string): RawSearch {
  const query = searchStr.startsWith('?') ? searchStr.substring(1) : searchStr;
  const result: RawSearch = {};
  query.split('&').filter(part => part !== '').forEach(part => {
    const index = part.indexOf('=');
    const key = decode(index === -1 ? part : part.substring(0, index));
    const value = index === -1 ? '' : decode(part.substring(index + 1));
    const previous = result[key];
    if (previous === undefined) {
      result[key] = value;
    } else if (Array.isArray(previous)) {
      previous.push(value);
    } else {
      result[key] = [previous, value];
    }
  });
  return result;
}

export function stringifySearch(search: Record<string, unknown>): string {
  const parts: string[] = [];
  Object.entries(search).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values
      .filter(entry => entry !== undefined && entry !== null)
      .forEach(entry => {
        const stringValue = typeof entry === 'object' ? JSON.stringify(entry) : String(entry);
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(stringValue)}`);
      });
  });
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

function searchString(value: unknown): string | undefined {
  const first = Array.isArray(value) ? value[0] : value;
  if (first === undefined || first === null || first === '') {
    return undefined;
  }
  return String(first);
}

function searchInt(value: unknown): number | undefined {
  const first = Array.isArray(value) ? value[0] : value;
  const parsed = typeof first === 'number' ? first : Number.parseInt(String(first ?? ''), 10);
  return Number.isInteger(parsed) ? parsed : undefined;
}

function searchBoolean(value: unknown): boolean | undefined {
  const first = Array.isArray(value) ? value[0] : value;
  if (typeof first === 'boolean') {
    return first;
  }
  return first === 'true' ? true : first === 'false' ? false : undefined;
}

const parsers = {string: searchString, int: searchInt, boolean: searchBoolean};

export type SearchParamType = keyof typeof parsers;

export type SearchParams<T extends Record<string, SearchParamType>> = {
  -readonly [K in keyof T]?: ReturnType<(typeof parsers)[T[K]]>;
};

export function searchParams<const T extends Record<string, SearchParamType>>(types: T) {
  return (search: Record<string, unknown>): SearchParams<T> => {
    const result: Record<string, unknown> = {};
    Object.entries(types).forEach(([key, type]) => {
      result[key] = parsers[type](search[key]);
    });
    return result as SearchParams<T>;
  };
}
